/* eslint-disable react-hooks/exhaustive-deps */

import { useMemo, useState } from 'react';

import { useAtom } from 'jotai';
import { isEmpty } from 'lodash-es';
import { useRouter } from 'next/router';
import { ImageListType } from 'react-images-uploading';

import {
  createAd,
  getCategory,
  getListDelegationByGovernorate,
  getNumberAdsByUser,
} from '@/api/fetch/marketplace-api';
import {
  DelegationsByGovernorateRequest,
  DelegationResponse,
  AdParam,
  AdParamValue,
  NewAdRequest,
  Location,
  NbAdsByUserRequest,
  AdResponse,
} from '@/api/gen-protos/ads_pb';
import {
  CategoryByIDRequest,
  CategoryResponse,
} from '@/api/gen-protos/categories_pb';
import { rudderstackTrackEvent } from '@/api/rudderstack/client';
import { CUSTOM_CATEGORY, SHOP_TYPE } from '@/lib/constants';
import { categories, governorates } from '@/lib/helpers';
import {
  ErorrsAtom,
  pageLoading,
  PageLoadingAction,
  SuccessAtom,
} from '@/lib/store';
import { User } from '@/models/user';
import useTransactions from '@/modules/profile/hooks/useTransactions';

import { mapCategoryResponseToIParams } from '../mappings';
import { UserDataAtom, AuthTokenAtom } from '../store/grpc-free-store';
import { SelectedCategory } from '../store/store';
import {
  IParams,
  ProductTypeEnum,
  SelectedIParams,
  SelectedIParamsList,
} from '../types';
import { uploadImages } from '../utils/utils';

const useCreateAdHook = (goToNextStep: () => void) => {
  const [category, setcategory] = useState<SelectedCategory>({});
  const [subcategory, setSubcategory] = useState<SelectedCategory>({});
  const [listSubcategory, setListSubcategory] = useState<SelectedCategory[]>(
    []
  );
  const [images, setImages] = useState<ImageListType>([]);
  const [title, setTitle] = useState('');
  const [newAdLoader, setNewAdLoader] = useState(false);
  const [newAdId, setNewAdId] = useState<string>('');
  const [description, setDesciption] = useState('');
  const [price, setPrice] = useState('0');
  const [paramsList, setParamList] = useState<IParams[]>([]);
  const [listDelegation, setListDelegation] = useState([]);
  const [selectedParamsList, setSelectedParamsList] =
    useState<SelectedIParamsList>({});
  const [governorate, setGovernorate] = useState<any>({});
  const [delegation, updateDelegation] = useState<any>({});
  const [phoneNumber, setphoneNumber] = useState('');
  const [, updateErrorsAtom] = useAtom(ErorrsAtom);
  const [, updateSuccessAtom] = useAtom(SuccessAtom);
  const [productType, setProductType] = useState(ProductTypeEnum.OCCASION);
  const [categoryPrice, setCategoryPrice] = useState(0);
  const [showConfirmDialog, setShowConfirmDialog] = useState<
    | {
        title: string;
        description: string;
        onClickConfirm: () => void;
      }
    | undefined
  >();

  const { balance } = useTransactions();
  const [useOwnPhone, setUseOnwPhone] = useState(false);

  const [userTotalAds, setUserTotalAds] = useState<{
    nbAds: number;
    defaultLimit: number;
  }>();
  const [userData] = useAtom<User>(UserDataAtom);
  const [authToken] = useAtom(AuthTokenAtom);
  const router = useRouter();

  const categoryList = useMemo(
    () =>
      categories
        .filter((cat) => {
          if (cat.parentcategoryxid === '') {
            if (cat.name === CUSTOM_CATEGORY) {
              if (userData.userType === SHOP_TYPE) {
                return cat;
              }
              return null;
            }
            return cat;
          }
          return null;
        })
        .map(({ id, name }) => ({ value: id, label: name })),
    [userData]
  );

  const [, setIsLoading] = useAtom(pageLoading);

  const updateErrors = ({
    message,
    tabName,
  }: {
    message: string;
    tabName: string;
  }) => updateErrorsAtom({ message, tabName, type: 'set' });

  const resetErrors = () => {
    updateErrorsAtom({ message: '', tabName: '', type: 'init' });
  };
  const getUserBalance = async (subcategoryId: string) => {
    if (subcategory && userData && userData.kcid) {
      const numberAdsByUser = new NbAdsByUserRequest();
      numberAdsByUser.setUserid(userData?.kcid);
      numberAdsByUser.setSubcategoryid(subcategoryId);
      getNumberAdsByUser(numberAdsByUser, authToken)
        .then((response) => {
          setUserTotalAds({
            nbAds: response?.getNbadsbyuser(),
            defaultLimit: response.getDefaultlimit(),
          });
        })
        .catch(() => {
          updateErrors({
            tabName: 'newAd_details',
            message:
              'Error lors de la récupération de votre solde, veuillez réessayer plus tard!',
          });
        });
    }
  };

  const selectCategory = (selectedCategory: SelectedCategory) => {
    resetErrors();
    updateSuccessAtom({ type: 'init', message: '', tabName: '' });

    if (!selectedCategory) return;
    setcategory(selectedCategory);
    setSubcategory({});
    const categoryByIDRequest = new CategoryByIDRequest();

    categoryByIDRequest.setId(selectedCategory.id as string);

    getCategory(categoryByIDRequest).then(
      (subCategoryParamsResponse: CategoryResponse) => {
        setCategoryPrice(
          subCategoryParamsResponse
            .getCategory()
            ?.getProductpriceMap()
            .get('insertions') as number
        );
      }
    );
    const ListSubCategory = categories.filter(
      (cat) => cat.parentcategoryxid === selectedCategory.id
    );

    setListSubcategory(ListSubCategory);
  };

  const selectSubCategory = async (selectedSubCategory: SelectedCategory) => {
    resetErrors();
    setParamList([]);
    setSelectedParamsList({});
    setSubcategory(selectedSubCategory);
    const categoryByIDRequest = new CategoryByIDRequest();

    categoryByIDRequest.setId(selectedSubCategory.id as string);

    getCategory(categoryByIDRequest).then(
      (subCategoryParamsResponse: CategoryResponse) => {
        setParamList(
          mapCategoryResponseToIParams(subCategoryParamsResponse) as IParams[]
        );
      }
    );

    getUserBalance(selectedSubCategory.id as string);
  };

  const updateSelectedOptions = ({
    name,
    type,
    possiblesValues,
    value,
  }: SelectedIParams) => {
    resetErrors();

    if (name) {
      if (value)
        setSelectedParamsList((prev) => ({
          ...prev,
          [name]: { name, type, possiblesValues, value },
        }));
      else {
        setSelectedParamsList((prev) => {
          const copyPrev = prev;
          delete copyPrev[name];

          return prev;
        });
      }
    }
    if (name === 'Marque') {
      setParamList((params) =>
        params.map((param) => {
          if (param.dependant === name) {
            return {
              ...param,
              possiblesValues: param.fixedPossiblesValues
                ?.filter((possibleValue) => possibleValue.startsWith(value))
                .map((modelWithPrefix) =>
                  modelWithPrefix.substring(value.length + 1)
                ),
              disabled: false,
            };
          }
          return param;
        })
      );
    }
    console.log(selectedParamsList);
  };

  const updateGovernorate = (selectedGovernorate: any) => {
    resetErrors();

    if (!selectedGovernorate) return;
    setGovernorate(selectedGovernorate);

    const delegationsByGovernorateRequest =
      new DelegationsByGovernorateRequest();
    delegationsByGovernorateRequest.setGovernoratexid(selectedGovernorate.id);
    getListDelegationByGovernorate(delegationsByGovernorateRequest).then(
      (data: DelegationResponse[]) => {
        const mappedListDelegation: any = [];
        // eslint-disable-next-line @typescript-eslint/no-shadow
        data.forEach((delegation: any) => {
          mappedListDelegation.push({
            id: delegation.getId(),
            name: delegation?.getDelegation().getName(),
          });
        });
        setListDelegation(mappedListDelegation);
      },
      () => {
        updateErrors({
          tabName: 'newAd_details',
          message:
            'Error lors de la récupération des délégations, veuillez réessayer plus tard!',
        });
      }
    );
  };

  function checkos() {
    const userAgent = navigator.userAgent || navigator.vendor;
    if (/windows phone/i.test(userAgent)) {
      return 'Windows Phone';
    }

    if (/android/i.test(userAgent)) {
      return 'Android';
    }

    if (/iPad|iPhone|iPod/.test(userAgent)) {
      return 'iOS';
    }

    return 'web';
  }

  const postAd = async () => {
    resetErrors();
    setIsLoading(PageLoadingAction.Start);
    setNewAdLoader(true);
    await uploadImages(images?.map((image) => image.file) as any, authToken)
      .then(async (data) => {
        if (data.status !== 200) {
          const jsonData = await data.json();
          throw jsonData.error;
        }

        return data.json();
      })
      .then(async (imagesUrl: any) => {
        if (imagesUrl?.message) {
          updateErrors({
            tabName: 'newAd_images',
            message: 'Erreur, veuillez insérer des images',
          });

          setIsLoading(PageLoadingAction.Stop);
          throw new Error();
        }
        const shopAvatar =
          userData.userType === SHOP_TYPE
            ? userData?.avatar || userData?.shopBackground
            : '';
        const newAdRequest = new NewAdRequest();
        newAdRequest.setImagesList(imagesUrl);
        newAdRequest.setUserid(userData.kcid as string);
        newAdRequest.setTitle(title);
        newAdRequest.setSubcategoryid(subcategory.id as string);
        newAdRequest.setDescription(description);
        newAdRequest.setPrice(parseInt(price, 10));
        newAdRequest.setProducttype(productType || 0);

        const location = new Location();
        location.setGovernorate(
          governorates.find((g) => governorate.name === g.name)?.dbvalue ||
            governorate.name
        );
        location.setDelegation(delegation.name);
        newAdRequest.setLocation(location);
        const AdParamsRequestData: AdParamValue[] = [];
        Object.keys(selectedParamsList).forEach((key: string) => {
          const { type, value, possibleValues } = selectedParamsList[
            key
          ] as any;
          const adParam = new AdParam();
          adParam.setName(key);
          adParam.setType(type);
          adParam.setPossiblevaluesList(possibleValues);
          const AdParamsValues = new AdParamValue();

          AdParamsValues.setParam(adParam);
          AdParamsValues.setValue(value);
          AdParamsRequestData.push(AdParamsValues);
        });
        newAdRequest.setAdparamvaluesList(AdParamsRequestData);
        newAdRequest.setUserphonenumber(
          (useOwnPhone ? userData?.phonenumber : `+216${phoneNumber}`) || ''
        );
        newAdRequest.setShopavatar(shopAvatar || '');
        console.log(newAdRequest.toObject());
        await createAd(authToken, newAdRequest)
          .then((res: AdResponse) => {
            rudderstackTrackEvent('Ad Insertion Completed', {
              title: newAdRequest.getTitle(),
              category: categories.find((c) => c.name === category.name)
                ?.dbvalue,
              price: newAdRequest.getPrice(),
              seller_id: newAdRequest.getUserid(),
              gouvernerate: newAdRequest.getLocation()?.getGovernorate(),
              delegation: newAdRequest.getLocation()?.getDelegation(),
              platform: checkos(),
            });

            setIsLoading(PageLoadingAction.Stop);
            setNewAdLoader(false);

            setNewAdId(res.getId());
            goToNextStep();
          })
          .catch((error) => {
            setNewAdLoader(false);

            if (error === 'not enough balance') {
              setShowConfirmDialog({
                title: "Vous n'avez pas de solde suffisant",
                description: `Vous avez dépassé la limite des annonces gratuites pour cette catégorie (${
                  category?.name
                }), achetez des jetons pour lister et booster des annonces. 
                Contactez nous au “95256201” pour plus de détails.
                 Il vous manque ${categoryPrice - balance} t-jetons`,
                onClickConfirm: () => router.push('/buy-tokens'),
              });
            } else {
              updateErrors({
                tabName: 'newAd_contact',
                message: ` Erreur lors de la création de l’annonce, Error : ${error}`,
              });
            }

            setIsLoading(PageLoadingAction.Stop);
          });
      })
      .catch((errorMsg) => {
        setNewAdLoader(false);

        updateErrors({ tabName: 'newAd_images', message: errorMsg });
        setIsLoading(PageLoadingAction.Stop);
      });
  };

  const onClickConfirmRunBuyToken = async () => {
    setShowConfirmDialog(undefined);
    await postAd();
  };

  const verifyInputForStep = (step: string) => {
    switch (step) {
      case 'details':
        return (
          !isEmpty(category) &&
          !isEmpty(subcategory) &&
          !isEmpty(price) &&
          !isEmpty(title) &&
          !isEmpty(description) &&
          !isEmpty(images) &&
          images.length > 0 &&
          (paramsList.length > 0 ? !isEmpty(selectedParamsList) : true)
        );

      case 'contact':
        return (
          !isEmpty(governorate) &&
          !isEmpty(delegation) &&
          !isEmpty(useOwnPhone ? userData?.phonenumber : phoneNumber)
        );

      default:
        return true;
    }
  };

  const startPostProcess = async () => {
    if (!verifyInputForStep('contact')) {
      updateErrors({
        tabName: 'newAd_contact',
        message: 'Veuillez remplir tout le formulaire!',
      });
      return;
    }
    if (
      !categoryPrice ||
      (userTotalAds && userTotalAds?.defaultLimit >= userTotalAds?.nbAds)
    ) {
      await postAd();
    } else if (balance < categoryPrice) {
      setShowConfirmDialog({
        title: "Vous n'avez pas de solde suffisant",
        description: `Vous avez dépassé la limite des annonces gratuites pour cette catégorie (${
          category?.name
        }), achetez des jetons pour lister et booster des annonces. 
                Contactez nous au “95256201” pour plus de détails.
                 Il vous manque ${categoryPrice - balance} t-jetons`,
        onClickConfirm: () => router.push('/buy-tokens'),
      });
    } else {
      setShowConfirmDialog({
        title: 'Votre annonce sera publiée',
        description: `${categoryPrice} t-jetons seront débités de votre solde.`,
        onClickConfirm: onClickConfirmRunBuyToken,
      });
    }
  };

  const setDelegation = (delegationProp: any) => {
    updateDelegation(delegationProp);
  };

  const confirmTokenPayment = () => {
    postAd();
  };

  return {
    category,
    setcategory,
    subcategory,
    setSubcategory,
    listSubcategory,
    setListSubcategory,
    paramsList,
    setParamList,
    selectedParamsList,
    setSelectedParamsList,
    governorate,
    delegation,
    setDelegation,
    phoneNumber,
    setphoneNumber,
    images,
    setImages,
    title,
    setTitle,
    description,
    setDesciption,
    price,
    startPostProcess,
    setPrice,
    categoryList,
    selectCategory,
    selectSubCategory,
    updateSelectedOptions,
    updateGovernorate,
    listDelegation,
    verifyInputForStep,

    showConfirmDialog,
    setShowConfirmDialog,
    confirmTokenPayment,
    useOwnPhone,
    setUseOnwPhone,
    newAdId,
    newAdLoader,
    productType,
    setProductType,
    updateErrors,
    resetErrors,
  };
};

export default useCreateAdHook;
